import { useState, useEffect, useCallback } from 'react'
import {
  AuthUser,
  getCurrentUser,
  logout as authLogout,
  redirectToLogin,
  exchangeCodeForTokens,
  storeTokens,
  getUserFromTokens,
} from '../services/auth'

interface UseAuthReturn {
  user: AuthUser | null
  isLoading: boolean
  isAuthenticated: boolean
  isAdmin: boolean
  login: () => void
  logout: () => void
  handleCallback: (code: string) => Promise<void>
}

export function useAuth(): UseAuthReturn {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // Check for existing session
    const currentUser = getCurrentUser()
    setUser(currentUser)
    setIsLoading(false)
  }, [])

  const login = useCallback(() => {
    redirectToLogin()
  }, [])

  const logout = useCallback(() => {
    setUser(null)
    authLogout()
  }, [])

  const handleCallback = useCallback(async (code: string) => {
    try {
      const tokens = await exchangeCodeForTokens(code)
      storeTokens(tokens)
      const newUser = getUserFromTokens(tokens)
      setUser(newUser)
    } catch (error) {
      console.error('Failed to exchange code for tokens:', error)
      throw error
    }
  }, [])

  return {
    user,
    isLoading,
    isAuthenticated: user !== null,
    isAdmin: user?.isAdmin ?? false,
    login,
    logout,
    handleCallback,
  }
}
